<!-- Patient_Gaps.vue -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 p-5">
    <!-- Loading -->
    <div v-if="loading" class="space-y-4">
      <div class="flex items-center justify-between mb-4">
        <div>
          <div class="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-3 w-28 bg-gray-100 rounded animate-pulse mt-1"></div>
        </div>
      </div>
      <div class="flex items-center space-x-2 mb-4 pt-3 border-t border-gray-100">
        <div v-for="n in 3" :key="'sk-filter-' + n" class="h-6 rounded-full bg-gray-200 animate-pulse" :style="{ width: [50, 56, 62][n - 1] + 'px' }"></div>
      </div>
      <div class="space-y-2">
        <div v-for="n in 4" :key="'sk-gap-' + n" class="rounded-lg border border-gray-200 px-3 py-2.5">
          <div class="flex items-center justify-between">
            <div class="space-y-1.5 flex-1">
              <div class="h-3 bg-gray-200 rounded animate-pulse" :style="{ width: [180, 140, 200, 160][n - 1] + 'px' }"></div>
              <div class="h-2.5 w-16 bg-gray-100 rounded animate-pulse"></div>
            </div>
            <div class="h-4 w-12 bg-gray-100 rounded-full animate-pulse ml-2"></div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <div>
          <div class="text-sm font-semibold text-gray-900">Care Gaps</div>
          <div class="text-xs text-gray-500 mt-0.5">
            <span class="font-medium text-gray-700">{{ openGaps.length }}</span> Open<template v-if="overduegaps.length > 0">
              · <span class="text-red-600"><span class="font-medium">{{ overduegaps.length }}</span> Overdue</span>
            </template>
          </div>
        </div>
        <button @click="loadGaps(_renewAbortSignal())" :disabled="loading" class="p-1.5 text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon :icon="['fas', 'sync']" class="text-xs" :class="{'animate-spin': loading}" />
        </button>
      </div>

      <!-- Filters -->
      <div class="flex items-center space-x-2 mb-4 pt-3 border-t border-gray-100">
        <button
          @click="gapFilter = 'all'"
          :class="[
            'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
            gapFilter === 'all'
              ? 'bg-gray-800 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
          ]"
        >
          All ({{ gaps.length }})
        </button>
        <button
          @click="gapFilter = 'open'"
          :class="[
            'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
            gapFilter === 'open'
              ? 'bg-orange-600 text-white'
              : 'bg-white text-orange-600 hover:bg-orange-50 border border-orange-300'
          ]"
        >
          Open ({{ openGaps.length }})
        </button>
        <button
          v-if="priorYearGaps.length > 0"
          @click="gapFilter = 'prior_year'"
          :class="[
            'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
            gapFilter === 'prior_year'
              ? 'bg-gray-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
          ]"
        >
          Prior Year ({{ priorYearGaps.length }})
        </button>
        <button
          @click="gapFilter = 'closed'"
          :class="[
            'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
            gapFilter === 'closed'
              ? 'bg-green-600 text-white'
              : 'bg-white text-green-600 hover:bg-green-50 border border-green-300'
          ]"
        >
          Closed ({{ closedGaps.length }})
        </button>
      </div>

      <!-- Empty State -->
      <div v-if="filteredGaps.length === 0" class="text-center py-6">
        <FontAwesomeIcon :icon="['fas', 'check-circle']" class="text-2xl text-green-200 mb-2" />
        <p class="text-gray-500 text-xs">
          <template v-if="gapFilter === 'all'">No care gaps found</template>
          <template v-else-if="gapFilter === 'open'">No open care gaps</template>
          <template v-else>No closed care gaps</template>
        </p>
      </div>

      <!-- Gaps List -->
      <div v-else class="space-y-2">
        <div
          v-for="gap in filteredGaps"
          :key="gap.id"
          @click="selectGap(gap)"
          class="group rounded-lg border hover:border-gray-300 cursor-pointer transition-all px-3 py-2.5"
          :class="getGapCardClass(gap)"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center space-x-2">
                <span class="text-xs font-medium text-gray-900 truncate">{{ gap.measure_name || 'Quality Measure' }}</span>
                <span
                  :class="[
                    'px-1.5 py-px text-[10px] rounded-full font-medium shrink-0',
                    gap.status === 'potentially_closed' ? 'bg-amber-100 text-amber-700' :
                    gap.status === 'excluded' ? 'bg-gray-200 text-gray-500' :
                    gap.status === 'prior_year' ? 'bg-gray-200 text-gray-500' :
                    gap.status === 'open' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                  ]"
                >
                  {{ gap.status === 'potentially_closed' ? 'Potentially Closed' :
                     gap.status === 'excluded' ? 'Excluded' :
                     gap.status === 'prior_year' ? 'Prior Year' :
                     gap.status === 'open' ? 'Open' : 'Closed' }}
                </span>
              </div>
              <!-- Current task stage. Only rendered when the gap's metric has a
                   task chain — care gaps without one simply show nothing here. -->
              <div v-if="gap.task_stage" class="flex items-center space-x-2 mt-1">
                <span class="px-1.5 py-px text-[10px] rounded font-medium bg-gray-100 text-gray-700 truncate">
                  <FontAwesomeIcon :icon="['fas', 'list-check']" class="mr-0.5" />
                  {{ gap.task_stage }}
                </span>
                <span v-if="gap.task_status" class="text-[10px] text-gray-500">{{ gap.task_status }}</span>
                <span v-if="gap.task_due_date" class="text-[10px]" :class="isTaskOverdue(gap) ? 'text-red-600 font-medium' : 'text-gray-500'">
                  Due {{ formatDate(gap.task_due_date) }}
                </span>
              </div>
              <div class="flex items-center space-x-3 text-[10px] text-gray-500 mt-0.5">
                <span v-if="gap.due_date">
                  <FontAwesomeIcon :icon="['fas', 'calendar']" class="mr-0.5" />
                  {{ formatDate(gap.due_date) }}
                </span>
                <span v-if="gap.measure_id">
                  <FontAwesomeIcon :icon="['fas', 'tag']" class="mr-0.5" />
                  {{ gap.measure_id }}
                </span>
                <span v-if="gap.clinical_evidence && gap.clinical_evidence.length > 0"
                      class="text-green-600 font-medium">
                  <FontAwesomeIcon :icon="['fas', 'flask-vial']" class="mr-0.5" />
                  {{ gap.clinical_evidence.length }}
                </span>
              </div>
            </div>
            <FontAwesomeIcon
              :icon="['fas', 'chevron-right']"
              class="text-[10px] text-gray-300 group-hover:text-gray-500 transition-colors ml-2"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import abortable from '@/mixins/abortable';

export default {
  name: 'Patient_Gaps',
  components: {
    FontAwesomeIcon
  },
  mixins: [abortable],

  props: {
    patientIdentifier: {
      type: String,
      required: true
    },
    entityId: {
      type: Number,
      default: null
    },
    includeExpired: {
      type: Boolean,
      default: false
    }
  },

  emits: ['show-message', 'gap-selected'],

  data() {
    return {
      loading: false,
      gaps: [],
      gapFilter: 'open'
    };
  },

  computed: {
    openGaps() {
      return this.gaps.filter(g => g.status === 'open' || g.status === 'potentially_closed');
    },

    priorYearGaps() {
      return this.gaps.filter(g => g.status === 'prior_year');
    },

    closedGaps() {
      return this.gaps.filter(g => g.status === 'closed');
    },

    overduegaps() {
      return this.openGaps.filter(g => g.is_overdue);
    },

    filteredGaps() {
      switch (this.gapFilter) {
        case 'open':
          return this.openGaps;
        case 'prior_year':
          return this.priorYearGaps;
        case 'closed':
          return this.closedGaps;
        default:
          return this.gaps;
      }
    }
  },

  watch: {
    patientIdentifier: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const signal = this._renewAbortSignal();
          this.loadGaps(signal);
        }
      }
    }
  },

  methods: {
    async loadGaps(signal) {
      if (!this.patientIdentifier) return;

      this.loading = true;
      try {
        const params = { patient_identifier: this.patientIdentifier };
        if (this.entityId) {
          params.entity_id = this.entityId;
        }
        if (this.includeExpired) {
          params.include_expired = true;
        }

        const response = await this.$axios.get('/api/quality/patient-gaps/', { params, signal });

        this.gaps = (response.data.gaps || []).map(gap => ({
          id: gap.id,
          measure_name: gap.measure_name,
          metric_name: gap.metric_name,
          measure_id: gap.measure_code,
          status: gap.status,
          due_date: gap.due_date,
          contract_name: gap.contract_name,
          payer_name: gap.payer_name,
          practice_name: gap.practice_name,
          action_type: gap.action_type,
          last_service_date: gap.last_service_date,
          evidence_value: gap.evidence_value,
          is_overdue: gap.is_overdue,
          provider_name: gap.provider_name,
          provider_address: gap.provider_address,
          lob: gap.lob,
          language: gap.language,
          bene_phone: gap.bene_phone,
          bene_address_1: gap.bene_address_1,
          bene_address_2: gap.bene_address_2,
          bene_city: gap.bene_city,
          bene_state: gap.bene_state,
          bene_zip: gap.bene_zip,
          patient_identifier: gap.patient_identifier,
          entity_id: gap.entity_id,
          opportunity_id: gap.opportunity_id,
          task_id: gap.task_id,
          task_type_id: gap.task_type_id,
          clinical_evidence: gap.clinical_evidence || [],
          // Surescripts pharmacy fill for med-adherence gaps. This map is an
          // explicit whitelist, so anything not listed here is dropped before
          // Quality_Gap_Detail ever sees it.
          surescripts_fill: gap.surescripts_fill || null,
          opportunity: {
            id: gap.opportunity_id,
            entity: gap.entity_id
          }
        }));
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Error loading care gaps:', error);
        this.$emit('show-message', 'Failed to load care gaps', 'error');
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },

    getGapCardClass(gap) {
      if (gap.status === 'closed') return 'border-green-200 bg-green-50/30';
      if (gap.status === 'potentially_closed') return 'border-amber-200 bg-amber-50/30';
      if (gap.status === 'excluded' || gap.status === 'prior_year') return 'border-gray-200 bg-gray-50/30';
      if (gap.is_overdue) return 'border-red-200 bg-red-50/30';
      return 'border-gray-200 bg-white';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    isTaskOverdue(gap) {
      // Snoozed tasks are not overdue (matches Patient_Task / Task_Side_Panel).
      if (!gap.task_due_date || gap.task_status === 'Snooze') return false;
      return new Date(gap.task_due_date + 'T23:59:59') < new Date();
    },

    selectGap(gap) {
      this.$emit('gap-selected', gap);
    }
  }
};
</script>

<style scoped>
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
